import { DEFAULT_SILVER_PRICE } from "@Zakat/helpers/zakat/consts";
import React, { FC, useState, createContext, useMemo } from "react";
import { ContextCurrencies } from "./constants";
import { Currency } from "@ifgengineering/hip-app-domain/src";

/** Uppercase currency codes matching all currencies available in ContextCurrencies */
export type CurrencyCode = "GBP" | "USD" | "EUR" | "INR" | "AED" | "CAD" | "MYR";

/** Type-safe mapping from Currency (lowercase) to CurrencyCode (uppercase) */
const CURRENCY_TO_CODE: Record<Currency, CurrencyCode> = {
  gbp: "GBP",
  usd: "USD",
  eur: "EUR",
  inr: "INR",
  aed: "AED",
  cad: "CAD",
  myr: "MYR",
} as const;

type ContextType = {
  currencySymbol: string;
  /** Uppercase currency code for use with component library (e.g., "GBP", "USD") */
  currencyCode: CurrencyCode;
  formatCurrency: (val: number) => string;
  setCurrency: React.Dispatch<React.SetStateAction<Currency>>;
  currency: Currency;
  silverPrice: number;
  setSilverPrice: React.Dispatch<React.SetStateAction<number>>;
};

// We can't have empty functions. We need to set a default here.
// So I'm create this informative one that does nothing
const emptyDefault = () => {
  console.log("You need to define a callback for it");
};

export const CurrencyContext = createContext<ContextType>({
  currencySymbol: "£",
  currencyCode: "GBP",
  formatCurrency: () => "gbp",
  currency: "gbp",
  setCurrency: emptyDefault,
  silverPrice: DEFAULT_SILVER_PRICE,
  setSilverPrice: emptyDefault,
});

export const moneyfy =
  (locale: string, config?: { noDecimals?: boolean }) =>
    (val: number): string => {
      const value = val || 0;
      const decimals = config?.noDecimals ? 0 : undefined;
      return value.toLocaleString("en", {
        style: "currency",
        currency: locale,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    };

export const CurrencyContextProvider: FC = ({ children }) => {
  const [currency, setCurrency] =
    useState<keyof typeof ContextCurrencies>("gbp");

  const [silverPrice, setSilverPrice] = useState<number>(DEFAULT_SILVER_PRICE);

  const currencySymbol =
    ContextCurrencies[(currency?.toLowerCase() as Currency) ?? "gbp"];

  const currencyCode = useMemo(
    () => CURRENCY_TO_CODE[currency],
    [currency]
  );

  return (
    <CurrencyContext.Provider
      value={{
        currencySymbol,
        currencyCode,
        formatCurrency: moneyfy(currency),
        currency,
        setCurrency,
        silverPrice,
        setSilverPrice,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
